/**
 * C12, "Edit / Delete planned event" (audit 06 § C12; TMMAPP-0112, the 0108 amendment): a planned
 * event's series scope as the prototype's "Apply to" radio cards — This event only / This and all
 * future events / The entire series, each with its note — then Continue (edit) or Delete. Edit hands
 * the scope to the series host (the occurrence sheet, or the series editor from this date or for the
 * whole series); Delete is the matching explicit verb (MM-D-48: cancel the occurrence, end the series
 * from this date, or cancel the series). Never a generic update.
 */
import { useRef, useState, type KeyboardEvent } from 'react';
import {
  Alert,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { plannedApi } from '@/api/planned';
import { SubmitButton } from '@/components/SubmitButton';
import { plannedErrorKey } from '@/features/planned/plannedErrors';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';

export type EventScope = 'occurrence' | 'following' | 'series';
const SCOPES: readonly EventScope[] = ['occurrence', 'following', 'series'];
const TITLE_ID = 'planned-event-title';

interface PlannedEventDialogProps {
  action: 'edit' | 'delete';
  name: string;
  seriesId: number;
  /** The occurrence's own date (`originalOn`). */
  originalOn: string;
  onClose: () => void;
  /** Edit: the scope the person chose; the host opens the sheet or the editor. */
  onEdit: (scope: EventScope) => void;
  /** Delete: done; the caller refetches the projection. */
  onDeleted: () => void | Promise<void>;
}

export function PlannedEventDialog({
  action,
  name,
  seriesId,
  originalOn,
  onClose,
  onEdit,
  onDeleted,
}: PlannedEventDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const [scope, setScope] = useState<EventScope>('occurrence');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A radio group: one tab stop (the checked card), the arrow keys move and choose.
  const cards = useRef<Array<HTMLButtonElement | null>>([]);

  function onCardKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (index + step + SCOPES.length) % SCOPES.length;
    cards.current[next]?.focus();
    setScope(SCOPES[next]!);
  }

  async function remove() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (scope === 'occurrence') await plannedApi.cancelOccurrence(seriesId, originalOn);
      else if (scope === 'following') await plannedApi.endFrom(seriesId, { fromDate: originalOn });
      else await plannedApi.cancelSeries(seriesId);
      await onDeleted();
    } catch (err) {
      setError(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
      setBusy(false);
    }
  }

  const card = (value: EventScope, index: number) => {
    const checked = scope === value;
    return (
      <ButtonBase
        key={value}
        ref={(node: HTMLButtonElement | null) => {
          cards.current[index] = node;
        }}
        role="radio"
        aria-checked={checked}
        tabIndex={checked ? 0 : -1}
        onKeyDown={(event) => onCardKeyDown(event, index)}
        aria-labelledby={`planned-event-${value}`}
        aria-describedby={`planned-event-${value}-note`}
        data-scope-option={value}
        onClick={() => setScope(value)}
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'start',
          p: '12px 14px',
          border: `1px solid ${checked ? tokens.brandBoundary : tokens.line}`,
          borderRadius: `${tokens.radius.control}px`,
          bgcolor: tokens.surfaceRaised,
          color: tokens.text,
          ...(checked
            ? { backgroundImage: `linear-gradient(${tokens.selected}, ${tokens.selected})` }
            : {}),
          '&.Mui-focusVisible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
        }}
      >
        <Typography
          id={`planned-event-${value}`}
          component="span"
          sx={{ display: 'block', fontSize: 14, fontWeight: 500 }}
        >
          {t(`plannedEvent.scope.${value}`)}
        </Typography>
        <Typography
          id={`planned-event-${value}-note`}
          component="span"
          sx={{ display: 'block', fontSize: 12, lineHeight: 1.5, mt: '2px', color: tokens.text2 }}
        >
          {t(`plannedEvent.scopeNote.${action}.${value}`, {
            date: formatCivilDate(originalOn, locale),
          })}
        </Typography>
      </ButtonBase>
    );
  };

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      aria-labelledby={TITLE_ID}
      maxWidth="xs"
      fullWidth
      sx={{ '& .MuiDialog-paper': { maxWidth: 460 } }}
    >
      <DialogTitle id={TITLE_ID}>
        {t(action === 'edit' ? 'plannedEvent.editTitle' : 'plannedEvent.deleteTitle')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: tokens.text2 }}>
          {t('plannedEvent.subtitle', { name, date: formatCivilDate(originalOn, locale) })}
        </Typography>
        <Typography
          id="planned-event-apply"
          variant="overline"
          component="p"
          sx={{ mt: 2, color: tokens.textLabel('surface') }}
        >
          {t('plannedEvent.applyTo')}
        </Typography>
        <Stack role="radiogroup" aria-labelledby="planned-event-apply" spacing={1} sx={{ mt: 1 }}>
          {SCOPES.map(card)}
        </Stack>
        {error !== null && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        {action === 'edit' ? (
          <SubmitButton onClick={() => onEdit(scope)}>{t('plannedEvent.continue')}</SubmitButton>
        ) : (
          <SubmitButton color="error" loading={busy} onClick={() => void remove()}>
            {t('plannedEvent.delete')}
          </SubmitButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
